import {
  ApiOperationType,
  CalculationResponse,
  Condition,
  EntityListCreateRequest,
  EntityListHoldersResponse,
  EntityListImportAction,
  EntityListImportItemResponse,
  EntityListImportItemsRequest,
  EntityListImportRequest,
  EntityListImportResponse,
  EntityListImportSummariesResponse,
  EntityListImportVerificationRequest,
  EntityListItemResponse,
  EntityListResponse,
  EntityListSourceType,
  EntityListTypeName,
  Operation,
  SortDirection,
  Sorting,
  ValidationProblemCode,
} from '@ydistri/api-sdk';
import { problemsOfError } from '../../Configuration/Validator/problemsLib';
import { VALIDATION_PROBLEMS_TAG } from '../../../apis/apiValidationProblems';
import { apiSlice, ErrorLike, ErrorType, errorTransform } from '../../../apis/api';
import { EntityListsCollection } from '../../../swagger/collections';
import { getTags } from '../../../apis/apiLib';
import {
  forceRefetchForInfiniteScroll,
  InfiniteScrollParams,
  mergeForInfiniteScroll,
  serializeInfiniteScrollParams,
  serializeQueryArgsForInfiniteScroll,
} from '@ydistri/ds';
import { SignalProjectConfigurationChanged } from '../common/administrationItemsTypes';
import { ReduxState } from '../../../store';
import { addToast } from '@ydistri/utils';
import { SignalConfigurationChanged, SignalScopeChanged } from '../../../signalr/signalrInterfaces';
import { signalrClient } from '../../../signalr/client';
import { isScopeSignalForProject } from '../../../apis/scopeSignalrCacheLib';
import { setSelectedEntityList } from './entityListAdministrationSlice';
import {
  EntityListTypeSupported,
  getEntityListSpecification,
  isEntityListTypeSupported,
} from './entityListsLib';
import { getSelectedProjectShortName, PROJECT_SLUG_HEADER } from '../../../apis/apiHeader';
import { QueryStatus } from '@reduxjs/toolkit/query';

const { TAGS, TAGS_ARRAY } = getTags('entityListsAdministration');

export const isEntityListImportValidationChanged = (error: unknown): boolean =>
  problemsOfError(error).some(
    problem => problem.code === ValidationProblemCode.EntityListImportValidationChanged,
  );

/** Running requests a scope change arrived during; each reloads its cache entry once more when it ends. */
const reloadAfterScopeChange = new Set<string>();

/**
 * Reloads a cache entry on each scope change of its project: a scope rule holds the lists it names, so a scope change
 * can change the holders and which lists may be deleted. A forced reload is skipped while one runs, and that one may
 * have read before the change, so it is marked to reload once more when it ends (reloadIfScopeChanged).
 */
const reloadOnScopeChange =
  (
    projectShortName: string | undefined,
    getCacheEntry: () => { status: QueryStatus; requestId?: string },
    reload: () => unknown,
  ) =>
  (signal: SignalScopeChanged): void => {
    if (
      !projectShortName ||
      !isScopeSignalForProject(signal.projectSlug, projectShortName) ||
      getSelectedProjectShortName() !== projectShortName
    )
      return;
    const entry = getCacheEntry();
    if (entry.status === QueryStatus.pending && entry.requestId) {
      reloadAfterScopeChange.add(entry.requestId);
    } else {
      reload();
    }
  };

/** Reloads the cache entry once more when a scope changed while this request ran. */
const reloadIfScopeChanged = async (
  requestId: string,
  queryFulfilled: Promise<unknown>,
  reload: () => unknown,
): Promise<void> => {
  const projectShortName = getSelectedProjectShortName();
  await queryFulfilled.catch(() => undefined);
  if (
    reloadAfterScopeChange.delete(requestId) &&
    getSelectedProjectShortName() === projectShortName
  ) {
    reload();
  }
};

export type EntityListUpdateRequest = { entityListTypeNames?: EntityListTypeName[] } & Pick<
  EntityListResponse,
  'entityListId' | 'scheduledArchiveOn'
> &
  Partial<Pick<EntityListResponse, 'name' | 'description' | 'isArchived'>>;

export interface GetEntityListsRequest {
  entityListType?: EntityListTypeName;
  sourceType?: EntityListSourceType;
  sortings?: Sorting[];
  conditions?: Condition[];
}

export const getDefaultEntityListsParams = (
  entityListType?: EntityListTypeName,
  sourceType?: EntityListSourceType,
): GetEntityListsRequest => ({
  entityListType,
  sourceType,
  sortings: [{ fieldName: 'CreatedAt', direction: SortDirection.Desc }],
});

export interface GetEntityListRequest {
  entityListType: EntityListTypeSupported;
  entityListId: number;
}

export interface GetLatestEntityListItemsRequest extends InfiniteScrollParams {
  entityListId: number;
}

export interface GetEntityListImportItemsRequest {
  entityListId: number;
  entityListImportAction: EntityListImportAction;
}

export interface CreateEntityListImportRequest extends EntityListImportRequest {
  entityListType: EntityListTypeSupported;
  entityListId: number;
}

export interface PutEntityListImportItemsRequest extends EntityListImportItemsRequest {
  entityListId: number;
}

export interface PutEntityListItemsRequest extends EntityListImportVerificationRequest {
  entityListId: number;
}

export interface DeleteEntityListImportRequest extends EntityListImportVerificationRequest {
  entityListType: EntityListTypeSupported;
  entityListId: number;
}

export interface DeleteEntityListRequest {
  entityListType: EntityListTypeSupported;
  entityListId: number;
}

export interface GetEntityListItemsCsvFileRequest extends GetLatestEntityListItemsRequest {
  compressed?: boolean; // if true, the file will be compressed
}

export const apiEntityLists = apiSlice
  .enhanceEndpoints({
    addTagTypes: TAGS_ARRAY,
  })
  .injectEndpoints({
    endpoints: builder => ({
      getEntityList: builder.query<EntityListResponse | undefined, GetEntityListRequest>({
        queryFn: async (arg, { dispatch, getState }) => {
          const data = await EntityListsCollection.entityListsList({
            type: arg.entityListType,
            conditions: [
              {
                fieldName: 'EntityListId',
                operation: Operation.Eq,
                value: arg.entityListId,
              },
            ],
          });

          if (data.data.data.length === 1) {
            const entityListData = data.data.data[0];
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const state = getState() as ReduxState;
            const queryKeys = apiEntityLists.util.selectCachedArgsForQuery(state, 'getEntityLists');

            queryKeys.forEach(qk => {
              dispatch(
                apiEntityLists.util.updateQueryData('getEntityLists', qk, draft => {
                  draft.forEach(item => {
                    if (item.entityListId === entityListData.entityListId) {
                      Object.assign(item, entityListData);
                    }
                  });
                }),
              );
            });

            return { data: data.data.data[0] };
          }
          return { data: undefined };
        },
        providesTags: (result, error, arg) => [{ type: TAGS.entityList, id: arg.entityListId }],
      }),

      getEntityLists: builder.query<EntityListResponse[], GetEntityListsRequest>({
        queryFn: async ({ entityListType, sourceType, conditions, sortings }) => {
          const data = await EntityListsCollection.entityListsList({
            type: entityListType ?? undefined,
            conditions: [
              ...(conditions ?? []),
              ...(sourceType
                ? [
                    {
                      fieldName: 'EntityListSourceTypeId',
                      operation: Operation.Eq,
                      value: sourceType,
                    },
                  ]
                : []),
            ],
            sortings,
          });

          return data.data;
        },
        providesTags: [TAGS.entityListsList],
        async onQueryStarted(arg, { dispatch, requestId, queryFulfilled }) {
          await reloadIfScopeChanged(requestId, queryFulfilled, () =>
            dispatch(
              apiEntityLists.endpoints.getEntityLists.initiate(arg, {
                subscribe: false,
                forceRefetch: true,
              }),
            ),
          );
        },
        async onCacheEntryAdded(arg, api) {
          const projectShortName = getSelectedProjectShortName();

          // A variant holds every list of its type; each screen decides which it shows, as for archived lists.
          const belongs = (entityList: EntityListResponse): boolean =>
            !arg.entityListType ||
            entityList.entityListTypes.some(elt => elt.entityListTypeName === arg.entityListType);

          // Deselect a list that left this type's cache or whose data were deleted, which the administration does not
          // show; a response that returns after a project switch leaves the next project's selection alone.
          const deselectIfSelected = (entityListId: number): void => {
            if (getSelectedProjectShortName() !== projectShortName) return;
            if (arg.entityListType && isEntityListTypeSupported(arg.entityListType)) {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
              const currentState = api.getState() as ReduxState;
              const selected =
                currentState.entityListsAdministration[arg.entityListType].selectedEntityList;
              if (selected?.entityListId === entityListId) {
                api.dispatch(
                  setSelectedEntityList({
                    entityListType: arg.entityListType,
                    data: undefined,
                  }),
                );
              }
            }
          };

          const signalerListener = (signal: SignalProjectConfigurationChanged) => {
            if (
              signal.projectSlug !== projectShortName ||
              getSelectedProjectShortName() !== projectShortName
            )
              return;
            if (signal.entityType !== 'EntityList') return;

            const entityListId = signal.entityId;
            api.dispatch(
              apiEntityLists.util.invalidateTags([
                { type: TAGS.entityList, id: entityListId },
                { type: TAGS.entityListItems, id: entityListId },
              ]),
            );

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const state = api.getState() as ReduxState;

            //if the currently selected target list was deleted,
            //we need to clear the selected target list
            if (arg.entityListType && isEntityListTypeSupported(arg.entityListType)) {
              const selectedEntityList =
                state.entityListsAdministration[arg.entityListType].selectedEntityList;
              if (signal.entityId === selectedEntityList?.entityListId) {
                if (signal.operationType === ApiOperationType.Delete) {
                  api.dispatch(
                    setSelectedEntityList({ entityListType: arg.entityListType, data: undefined }),
                  );
                  api.dispatch(
                    addToast({
                      message: `${getEntityListSpecification(arg.entityListType).title} has been deleted`,
                    }),
                  );
                }
              }
            }

            if (
              signal.operationType === ApiOperationType.Create ||
              signal.operationType === ApiOperationType.Patch
            ) {
              EntityListsCollection.getEntityList(entityListId).then(newEntityListData => {
                const newEntityList = newEntityListData.data.data;

                const belongsHere = belongs(newEntityList);

                api.updateCachedData(draft => {
                  const entityListIndex = draft.findIndex(
                    entityList => entityList.entityListId === entityListId,
                  );

                  if (entityListIndex !== -1) {
                    if (belongsHere) {
                      draft[entityListIndex] = newEntityList;
                    } else {
                      draft.splice(entityListIndex, 1);
                    }
                  } else if (belongsHere) {
                    draft.push(newEntityList);
                  }
                });

                if (!belongsHere || newEntityList.isDataDeleted) {
                  deselectIfSelected(entityListId);
                }
              });
            } else if (signal.operationType === ApiOperationType.Delete) {
              api.updateCachedData(draft => {
                return draft.filter(e => e.entityListId !== entityListId);
              });
            } else {
              // eslint-disable-next-line no-console -- we output this for reference
              console.log('Entity lists: Other operation type', signal.operationType);
            }
          };

          /**
           * React to changes in the template configuration when target list is set to or removed from a template.
           * Based on this change the target list might be marked as archivable or not and list of templates
           * where it is used will change.
           * The listener fetches the target list from the server and updates the cache.
           * @param signal
           */
          const templateConfigurationListener = (signal: SignalConfigurationChanged) => {
            if (
              signal.projectSlug !== projectShortName ||
              getSelectedProjectShortName() !== projectShortName
            )
              return;
            // eslint-disable-next-line no-console -- we output the signal for reference
            console.log('templateConfigurationListener', signal);
            if (signal.entityType === 'EntityListConfiguration') {
              const entityListId = signal.entityId;
              EntityListsCollection.entityListsList({
                conditions: [
                  {
                    fieldName: 'EntityListId',
                    operation: Operation.Eq,
                    value: entityListId,
                  },
                ],
              })
                .then(response => {
                  try {
                    api.dispatch(
                      apiEntityLists.util.invalidateTags([
                        { type: TAGS.entityListCalculations, id: entityListId },
                      ]),
                    );

                    const tmpEntityList = response.data.data[0];
                    const belongsHere = belongs(tmpEntityList);
                    api.updateCachedData(data => {
                      const entityListIndex = data.findIndex(
                        tl => tl.entityListId === tmpEntityList.entityListId,
                      );
                      if (entityListIndex !== -1) {
                        if (belongsHere) {
                          data[entityListIndex] = tmpEntityList;
                        } else {
                          data.splice(entityListIndex, 1);
                        }
                      }
                    });
                    if (!belongsHere || tmpEntityList.isDataDeleted) {
                      deselectIfSelected(tmpEntityList.entityListId);
                    }
                  } catch (error) {
                    // eslint-disable-next-line no-console -- we output the error for reference
                    console.error(error);
                    api.dispatch(
                      addToast({
                        message: `Failed to update the list, refresh the page.`,
                        isError: true,
                      }),
                    );
                  }
                })
                // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- we know it's the right type
                .catch((error: ErrorType) => {
                  api.dispatch(
                    addToast({
                      message: `Failed to load list: ${error.response.data.Messages.join(', ')}`,
                      isError: true,
                    }),
                  );
                });
            }
          };

          const scopeListener = reloadOnScopeChange(
            projectShortName,
            () => api.getCacheEntry(),
            () =>
              api.dispatch(
                apiEntityLists.endpoints.getEntityLists.initiate(arg, {
                  subscribe: false,
                  forceRefetch: true,
                }),
              ),
          );
          // Listens from the first load on, so a scope change while it runs is not lost.
          signalrClient.on('ScopeChanged', scopeListener);

          try {
            await api.cacheDataLoaded;
            signalrClient.on('ProjectConfigurationChanged', signalerListener);
            signalrClient.on('TemplateConfigurationChanged', templateConfigurationListener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('ProjectConfigurationChanged', signalerListener);
              signalrClient.off('TemplateConfigurationChanged', templateConfigurationListener);
            });
            await api.cacheEntryRemoved;
          } catch (error) {
            // eslint-disable-next-line no-console -- we output the error for reference
            console.error(error);
          } finally {
            signalrClient.off('ScopeChanged', scopeListener);
          }
        },
      }),

      createEntityList: builder.mutation<EntityListResponse, EntityListCreateRequest>({
        queryFn: async payload => {
          try {
            const response = await EntityListsCollection.createEntityList(payload);
            const newEntityList = response.data.data;
            return { data: newEntityList };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        invalidatesTags: [{ type: VALIDATION_PROBLEMS_TAG }],
      }),

      createEntityListImport: builder.mutation<
        EntityListImportResponse,
        CreateEntityListImportRequest
      >({
        queryFn: async ({ entityListId, entityListType, ...payload }, { dispatch, getState }) => {
          try {
            const response = await EntityListsCollection.createEntityListImport(entityListId, {
              ...payload,
            });
            const data = response.data.data;

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- state is of this type
            const state = getState() as ReduxState;
            const queryKeys = apiEntityLists.util.selectCachedArgsForQuery(state, 'getEntityLists');

            queryKeys.forEach(qk => {
              dispatch(
                apiEntityLists.util.updateQueryData('getEntityLists', qk, draft =>
                  draft.map(el =>
                    el.entityListId === entityListId ? { ...el, entityListImport: data } : el,
                  ),
                ),
              );
            });

            dispatch(
              apiEntityLists.util.updateQueryData(
                'getEntityList',
                { entityListType, entityListId },
                draft => {
                  if (draft) draft.entityListImport = data;
                },
              ),
            );

            return { data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        // invalidatesTags: [TAGS.entityListsList],
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- returns no response
      deleteEntityListImport: builder.mutation<void, DeleteEntityListImportRequest>({
        queryFn: async ({ entityListId, entityListType, ...payload }, { dispatch, getState }) => {
          try {
            const { data } = await EntityListsCollection.deleteEntityListImport(entityListId, {
              ...payload,
            });

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const state = getState() as ReduxState;
            const queryKeys = apiEntityLists.util.selectCachedArgsForQuery(state, 'getEntityLists');

            queryKeys.forEach(qk => {
              dispatch(
                apiEntityLists.util.updateQueryData('getEntityLists', qk, draft =>
                  draft.map(el =>
                    el.entityListId === entityListId ? { ...el, entityListImport: null } : el,
                  ),
                ),
              );
            });

            dispatch(
              apiEntityLists.util.updateQueryData(
                'getEntityList',
                { entityListType, entityListId },
                draft => {
                  if (draft) draft.entityListImport = null;
                },
              ),
            );

            return { data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
            const err = error as ErrorType;
            return { error: err };
          }
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- returns no response
      putEntityListImportItems: builder.mutation<void, PutEntityListImportItemsRequest>({
        queryFn: async ({ entityListId, ...payload }) => {
          try {
            const { data } = await EntityListsCollection.putEntityListImportItems(entityListId, {
              ...payload,
            });

            return { data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it is the right type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        invalidatesTags: [TAGS.entityListImportSummary],
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- returns no response
      putEntityListItems: builder.mutation<void, PutEntityListItemsRequest>({
        queryFn: async ({ entityListId, ...payload }) => {
          try {
            const { data } = await EntityListsCollection.putEntityListItems(entityListId, {
              ...payload,
            });

            return { data };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it is the right type
            const err = error as ErrorType;
            return { error: err };
          }
        },
        // The list is committed here; attributes become ready only when the wrapper finishes.
        invalidatesTags: (result, error, arg) => {
          if (error) {
            return isEntityListImportValidationChanged(error)
              ? [
                  { type: TAGS.entityListImportSummary, id: arg.entityListId },
                  { type: TAGS.entityListImportItems, id: arg.entityListId },
                ]
              : [];
          }
          return [
            TAGS.entityListsList,
            { type: TAGS.entityListItems, id: arg.entityListId },
            { type: TAGS.entityList, id: arg.entityListId },
          ];
        },
      }),

      deleteEntityList: builder.mutation<number, DeleteEntityListRequest>({
        queryFn: async ({ entityListId }, { dispatch, getState }) => {
          await EntityListsCollection.deleteEntityList(entityListId);

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's the right type
          const state = getState() as ReduxState;
          const queryKeys = apiEntityLists.util.selectCachedArgsForQuery(state, 'getEntityLists');

          queryKeys.forEach(qk => {
            dispatch(
              apiEntityLists.util.updateQueryData('getEntityLists', qk, draft =>
                draft.filter(entityList => entityList.entityListId !== entityListId),
              ),
            );
          });

          return { data: entityListId };
        },
        invalidatesTags: [{ type: VALIDATION_PROBLEMS_TAG }],
      }),

      previewEntityListUnarchive: builder.mutation<
        boolean,
        { entityListId: number; projectShortName: string }
      >({
        queryFn: async ({ entityListId, projectShortName }) => {
          try {
            const response = await EntityListsCollection.getEntityListUnarchivePreview(
              entityListId,
              { headers: { [PROJECT_SLUG_HEADER]: projectShortName } },
            );
            return { data: response.data.data.isComputationRequired };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- SDK errors may have an HTTP envelope
            return { error: errorTransform(error as ErrorLike) };
          }
        },
      }),

      unarchiveEntityList: builder.mutation<
        EntityListResponse,
        GetEntityListRequest & { projectShortName: string; isComputationConfirmed: boolean }
      >({
        queryFn: async (
          { entityListId, entityListType, projectShortName, isComputationConfirmed },
          { dispatch, getState },
        ) => {
          if (getSelectedProjectShortName() !== projectShortName) {
            return { error: errorTransform({ message: 'The selected project has changed.' }) };
          }
          try {
            const response = await EntityListsCollection.updateEntityList(
              entityListId,
              { isArchived: false, isComputationConfirmed },
              { headers: { [PROJECT_SLUG_HEADER]: projectShortName } },
            );
            const result = response.data.data;
            if (getSelectedProjectShortName() === projectShortName) {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the endpoint uses the application store
              const state = getState() as ReduxState;
              if (
                state.entityListsAdministration[entityListType].selectedEntityList?.entityListId ===
                entityListId
              ) {
                dispatch(setSelectedEntityList({ entityListType, data: result }));
              }
              dispatch(
                apiSlice.util.invalidateTags([
                  TAGS.entityListsList,
                  { type: TAGS.entityList, id: entityListId },
                  VALIDATION_PROBLEMS_TAG,
                ]),
              );
            }
            return { data: result };
          } catch (error) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- SDK errors may have an HTTP envelope
            return { error: errorTransform(error as ErrorLike) };
          }
        },
      }),

      updateEntityList: builder.mutation<EntityListResponse | undefined, EntityListUpdateRequest>({
        queryFn: async (request: EntityListUpdateRequest) => {
          const response = await EntityListsCollection.updateEntityList(request.entityListId, {
            ...request,
          });
          const result = response.data.data;
          return { data: result };
        },
        // A list archived, renamed or retyped changes what the categories and product lists reports
        // of every template that references it say.
        invalidatesTags: [{ type: VALIDATION_PROBLEMS_TAG }],
      }),

      getEntityListItems: builder.query<EntityListItemResponse[], GetLatestEntityListItemsRequest>({
        queryFn: async args => {
          const query = {
            top: args.top,
            skip: args.skip,
            inlineCount: true,
            sortings: args.sortings,
            conditions: args.conditions,
            search: args.search,
          };

          const response = await EntityListsCollection.getLatestEntityListItems(
            args.entityListId,
            query,
          );
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.entityListItems, id: arg.entityListId },
        ],
        serializeQueryArgs: serializeQueryArgsForInfiniteScroll<GetLatestEntityListItemsRequest>(
          getSelectedProjectShortName,
        ),
        merge: mergeForInfiniteScroll<EntityListItemResponse, InfiniteScrollParams>(),
        forceRefetch: forceRefetchForInfiniteScroll<InfiniteScrollParams | undefined>(),
      }),

      getEntityListCalculations: builder.query<CalculationResponse[], number>({
        queryFn: async entityListId => {
          const response = await EntityListsCollection.getEntityListCalculations(entityListId, {
            sortings: [
              {
                fieldName: 'Title',
                direction: SortDirection.Asc,
              },
            ],
          });

          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [{ type: TAGS.entityListCalculations, id: arg }],
      }),

      getEntityListHolders: builder.query<EntityListHoldersResponse, number>({
        queryFn: async entityListId => {
          const response = await EntityListsCollection.getEntityListHolders(entityListId);

          return { data: response.data.data };
        },
        async onQueryStarted(entityListId, { dispatch, requestId, queryFulfilled }) {
          await reloadIfScopeChanged(requestId, queryFulfilled, () =>
            dispatch(
              apiEntityLists.endpoints.getEntityListHolders.initiate(entityListId, {
                subscribe: false,
                forceRefetch: true,
              }),
            ),
          );
        },
        async onCacheEntryAdded(entityListId, api) {
          const scopeListener = reloadOnScopeChange(
            getSelectedProjectShortName(),
            () => api.getCacheEntry(),
            () =>
              api.dispatch(
                apiEntityLists.endpoints.getEntityListHolders.initiate(entityListId, {
                  subscribe: false,
                  forceRefetch: true,
                }),
              ),
          );
          signalrClient.on('ScopeChanged', scopeListener);
          try {
            await api.cacheEntryRemoved;
          } finally {
            signalrClient.off('ScopeChanged', scopeListener);
          }
        },
      }),

      getEntityListImportSummary: builder.query<EntityListImportSummariesResponse, number>({
        queryFn: async entityListId => {
          const response = await EntityListsCollection.getEntityListImportSummary(entityListId);

          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [{ type: TAGS.entityListImportSummary, id: arg }],
      }),

      getEntityListImportItems: builder.query<
        EntityListImportItemResponse[],
        GetEntityListImportItemsRequest
      >({
        queryFn: async arg => {
          const query = {
            top: 500,
            skip: 0,
            inlineCount: true,
            conditions: [
              {
                fieldName: 'EntityListImportActionCode',
                operation: Operation.Eq,
                value: arg.entityListImportAction,
              },
            ],
          };

          const response = await EntityListsCollection.getEntityListImportItems(
            arg.entityListId,
            query,
          );

          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          { type: TAGS.entityListImportItems, id: arg.entityListId },
          {
            type: TAGS.entityListImportItems,
            id: `${arg.entityListId}-${arg.entityListImportAction}`,
          },
        ],
      }),
      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- result may be void here
      getEntityListItemsCsvFile: builder.query<Blob | void, GetEntityListItemsCsvFileRequest>({
        queryFn: async arg => {
          const response = await EntityListsCollection.getLatestEntityListItemsCsvFile(
            arg.entityListId,
            { format: 'blob' },
          );
          const blob = response.data;
          return { data: blob };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.entityListItemsCsv,
            id: `productList: ${arg.entityListId} (${serializeInfiniteScrollParams(arg)}_compressed(${arg.compressed}))`,
          },
        ],
      }),
    }),
  });

export const {
  useGetEntityListsQuery,
  useGetEntityListQuery,
  useLazyGetEntityListQuery,
  useUpdateEntityListMutation,
  usePreviewEntityListUnarchiveMutation,
  useUnarchiveEntityListMutation,
  useCreateEntityListMutation,
  useDeleteEntityListMutation,
  useGetEntityListItemsQuery,
  usePutEntityListItemsMutation,
  useGetEntityListCalculationsQuery,
  useGetEntityListHoldersQuery,
  useGetEntityListImportSummaryQuery,
  useCreateEntityListImportMutation,
  useDeleteEntityListImportMutation,
  useGetEntityListImportItemsQuery,
  usePutEntityListImportItemsMutation,
  useLazyGetEntityListItemsCsvFileQuery,
} = apiEntityLists;
